
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { logWithTimestamp, logAuthEvent, AuthEvent, isAdminEmail } from '../utils/authUtils';
import { getValidUserTypes } from '../utils/userTypeUtils';

/**
 * Authentication service to centralize all auth-related functionality
 */
export class AuthService {
  /**
   * Sign in with email and password
   */
  static async signInWithEmail(email: string, password: string) {
    logWithTimestamp('Attempting email sign in');
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (error) {
        logWithTimestamp('Email sign in error:', error);
        throw error;
      }

      logAuthEvent(AuthEvent.SIGNED_IN, data.user?.id, { provider: 'email' });
      logWithTimestamp('Email sign in successful');
      
      return { data, error: null };
    } catch (error: any) {
      logWithTimestamp('Exception during email sign in:', error);
      return { data: null, error };
    }
  }
  
  /**
   * Sign up with email and password
   */
  static async signUpWithEmail(email: string, password: string, userType: string = 'standard') {
    logWithTimestamp('Attempting email sign up');
    
    try {
      // Validate user type
      const validUserTypes = await getValidUserTypes();
      if (!validUserTypes.includes(userType)) {
        userType = 'standard'; // Default to standard if invalid type provided
      }
      
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          data: { 
            user_type: userType 
          }
        }
      });

      if (error) {
        logWithTimestamp('Email sign up error:', error);
        throw error;
      }

      logAuthEvent(AuthEvent.SIGNED_IN, data.user?.id, { 
        provider: 'email', 
        isSignUp: true,
        userType 
      });
      
      logWithTimestamp('Email sign up successful');
      
      return { 
        data, 
        error: null, 
        confirmEmail: !data.session 
      };
    } catch (error: any) {
      logWithTimestamp('Exception during email sign up:', error);
      return { data: null, error, confirmEmail: false };
    }
  }

  /**
   * Sign out the current user
   */
  static async signOut() {
    logWithTimestamp('Signing out user');
    
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        logWithTimestamp('Sign out error:', error);
        throw error;
      }
      
      logWithTimestamp('Sign out successful');
      logAuthEvent(AuthEvent.SIGNED_OUT, null);
      
      return { error: null };
    } catch (error: any) {
      logWithTimestamp('Exception during sign out:', error);
      return { error };
    }
  }

  /**
   * Request a password reset for the given email
   */
  static async resetPassword(email: string) {
    logWithTimestamp(`Requesting password reset for email: ${email}`);
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?type=recovery`,
      });

      if (error) {
        logWithTimestamp('Password reset request error:', error);
        throw error;
      }

      logWithTimestamp('Password reset email sent successfully');
      return { error: null, sent: true };
    } catch (error: any) {
      logWithTimestamp('Exception during password reset request:', error);
      return { error, sent: false };
    }
  }

  /**
   * Update password for the current user
   */
  static async updatePassword(newPassword: string) {
    logWithTimestamp('Updating password');
    
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });

      if (error) {
        logWithTimestamp('Password update error:', error);
        throw error;
      }

      logWithTimestamp('Password updated successfully');
      return { error: null, updated: true };
    } catch (error: any) {
      logWithTimestamp('Exception during password update:', error);
      return { error, updated: false };
    }
  }

  /**
   * Get the current session
   */
  static async getSession() {
    try {
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        logWithTimestamp('Get session error:', error);
        throw error;
      }
      
      return { session: data.session, error: null };
    } catch (error: any) {
      logWithTimestamp('Exception during get session:', error);
      return { session: null, error };
    }
  }

  /**
   * Check if the current user is an admin
   */
  static isAdmin(user: User | null): boolean {
    if (!user) return false;
    
    // Check by email first (most reliable)
    if (isAdminEmail(user.email)) {
      return true;
    }
    
    // Then check by user metadata
    const userType = 
      user.user_metadata?.user_type || 
      user.user_metadata?.data?.user_type;
    
    return userType === 'admin';
  }

  /**
   * Ensure a user profile exists
   * @param userId The ID of the user
   * @returns A promise that resolves when the profile check/creation is complete
   */
  static async ensureUserProfile(userId: string, accessToken: string) {
    try {
      logWithTimestamp('Ensuring user profile exists');
      
      // Call the handle-signup function to ensure profile exists
      const { data, error } = await supabase.functions.invoke('handle-signup', {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      
      if (error) {
        logWithTimestamp('Error ensuring user profile:', error);
        throw error;
      }
      
      logWithTimestamp('Profile check complete:', data);
      return { data, error: null };
    } catch (error: any) {
      logWithTimestamp('Exception ensuring user profile:', error);
      return { data: null, error };
    }
  }
}
